import React, { useEffect, useState } from "react";
import { Trash2, Plus, User } from "lucide-react";
import { useAuth } from "../Contexts/AuthContext";
import TokenChecker from './TokenChecker'

const API_PREFIX = "/api/settings";

const jsonOrThrow = async (res) => {
  let data = null;
  try { data = await res.json(); } catch (_) {}
  if (!res.ok) {
    const msg = (data && (data.detail || data.error)) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
};

export default function SettingsPage() {
  const { authData, logout, updateProfile } = useAuth();

  const [accountData, setAccountData] = useState({
    firstName: authData.firstName || "",
    lastName: authData.lastName || "",
    email: authData.email || "",
  });
  const [savingAccount, setSavingAccount] = useState(false);
  const [emailChangeNotice, setEmailChangeNotice] = useState("");
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileStep, setProfileStep] = useState(1);
  const [profilePassword, setProfilePassword] = useState("");
  const [profileFirstName, setProfileFirstName] = useState("");
  const [profileLastName, setProfileLastName] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [profileMessage, setProfileMessage] = useState("");

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordStep, setPasswordStep] = useState(1);
  const [passwordCurrent, setPasswordCurrent] = useState("");
  const [passwordNew, setPasswordNew] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");

  // ------------ Trusted Users ------------
  const [trustedUsers, setTrustedUsers] = useState([]);
  const [loadingTrusted, setLoadingTrusted] = useState(false);
  const [addingTrusted, setAddingTrusted] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [banner, setBanner] = useState({ type: "", msg: "" });

  const [newTrustedUser, setNewTrustedUser] = useState({ email: "" });

  // ------------ Notification Preferences ------------
  const [soundEnabled, setSoundEnabled] = useState(true);
  useEffect(() => {
    const v = localStorage.getItem('notificationSoundEnabled');
    if (v === null) {
      setSoundEnabled(true); // default on
    } else {
      setSoundEnabled(v === 'true');
    }
  }, []);
  const handleSoundToggle = (checked) => {
    setSoundEnabled(checked);
    localStorage.setItem('notificationSoundEnabled', String(checked));
    try {
      window.dispatchEvent(new CustomEvent('flowman:notification-sound', { detail: { enabled: checked } }));
    } catch (_) {}
  };

  const normalizeEmail = (value) => (value || "").trim().toLowerCase();
  const isValidGmail = (value) => {
    const normalized = normalizeEmail(value);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) return false;
    return normalized.endsWith("@gmail.com");
  };

  useEffect(() => {
    setAccountData({
      firstName: authData.firstName || "",
      lastName: authData.lastName || "",
      email: authData.email || "",
    });
  }, [authData.firstName, authData.lastName, authData.email]);

  const openProfileModal = () => {
    setProfilePassword("");
    setProfileFirstName(authData.firstName || "");
    setProfileLastName(authData.lastName || "");
    setProfileEmail(authData.email || "");
    setProfileMessage("");
    setProfileStep(1);
    setShowProfileModal(true);
  };

  const closeProfileModal = () => {
    if (savingAccount) return;
    setShowProfileModal(false);
    setProfileStep(1);
    setProfilePassword("");
    setProfileMessage("");
  };

  const handleProfilePasswordNext = () => {
    if (!profilePassword) {
      alert("Please enter your current password.");
      return;
    }
    setProfileStep(2);
  };

  const handleSaveProfile = async () => {
    if (savingAccount) return;
    const normalizedEmail = normalizeEmail(profileEmail);
    const currentEmail = normalizeEmail(authData.email);
    const emailChanged = normalizedEmail && normalizedEmail !== currentEmail;
    const nameChanged = profileFirstName.trim() !== (authData.firstName || "") ||
      profileLastName.trim() !== (authData.lastName || "");

    if (!emailChanged && !nameChanged) {
      alert("No changes to save.");
      return;
    }

    if (emailChanged && !isValidGmail(normalizedEmail)) {
      alert("Please use a valid Gmail address (e.g., name@gmail.com).");
      return;
    }

    setSavingAccount(true);
    try {
      if (nameChanged) {
        const res = await TokenChecker(`${API_PREFIX}/change-profile/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: profileFirstName.trim(),
            lastName: profileLastName.trim(),
            currentPassword: profilePassword,
          }),
        });
        await jsonOrThrow(res);
        updateProfile({ firstName: profileFirstName.trim(), lastName: profileLastName.trim() });
      }

      let message = "";
      if (emailChanged) {
        const res = await TokenChecker(`${API_PREFIX}/change-email/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ newEmail: normalizedEmail, currentPassword: profilePassword }),
        });
        const data = await jsonOrThrow(res);
        message = data.message || "Verification link sent to your new email.";
        setEmailChangeNotice(
          `Verification link sent to ${normalizedEmail}. Your current email stays active until confirmed.`
        );
      }

      if (nameChanged && message) {
        message = `Name updated. ${message}`;
      } else if (!message && nameChanged) {
        message = "Your name has been updated successfully.";
      }

      if (message) {
        setProfileMessage(message);
        setProfileStep(3);
      } else {
        closeProfileModal();
      }
    } catch (e) {
      alert(e.message || "Failed to update account settings.");
    } finally {
      setSavingAccount(false);
    }
  };

  const openPasswordModal = () => {
    setPasswordCurrent("");
    setPasswordNew("");
    setPasswordConfirm("");
    setPasswordMessage("");
    setPasswordStep(1);
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    if (savingAccount) return;
    setShowPasswordModal(false);
    setPasswordStep(1);
    setPasswordCurrent("");
    setPasswordNew("");
    setPasswordConfirm("");
    setPasswordMessage("");
  };

  const handlePasswordNext = () => {
    if (!passwordCurrent) {
      alert("Please enter your current password.");
      return;
    }
    setPasswordStep(2);
  };

  const handleSavePassword = async () => {
    if (savingAccount) return;
    if (!passwordNew || !passwordConfirm) {
      alert("Please fill in all password fields.");
      return;
    }
    if (passwordNew !== passwordConfirm) {
      alert("New password and confirmation do not match.");
      return;
    }
    if (passwordNew.length < 8) {
      alert("New password must be at least 8 characters long.");
      return;
    }
    setSavingAccount(true);
    try {
      const res = await TokenChecker(`${API_PREFIX}/change-password/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentPassword: passwordCurrent,
          newPassword: passwordNew,
          confirmPassword: passwordConfirm,
        }),
      });
      const data = await jsonOrThrow(res);
      setPasswordMessage(data.message || "Password updated successfully.");
      setPasswordStep(3);
    } catch (e) {
      alert(e.message || "Failed to update password.");
    } finally {
      setSavingAccount(false);
    }
  };

  // Fetch my trusted users
  const fetchTrustedUsers = async () => {
    setLoadingTrusted(true);
    setBanner({ type: "", msg: "" });
    try {
      const res = await TokenChecker(`${API_PREFIX}/trusted-users/`, { method: "GET" });
      const data = await jsonOrThrow(res);
      // data = [{ id, user:{}, trusted:{ id, email, first_name, last_name }, created_at }]
      const mapped = (Array.isArray(data) ? data : []).map((row) => {
        const t = row.trusted || {};
        const name = [t.first_name, t.last_name].filter(Boolean).join(" ").trim() || t.email || "User";
        return {
          id: t.id, // use trusted user's id as the UI id (needed for DELETE path)
          name,
          email: t.email || "",
          addedDate: (row.created_at || "").slice(0, 10),
        };
      });
      setTrustedUsers(mapped);
    } catch (e) {
      setBanner({ type: "error", msg: e.message || "Failed to load trusted users." });
    } finally {
      setLoadingTrusted(false);
    }
  };

  useEffect(() => {
    fetchTrustedUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add a trusted user by email
  const handleAddTrustedUser = async () => {
    if (!newTrustedUser.email) {
      setBanner({ type: "error", msg: "Please enter an email." });
      return;
    }
    setAddingTrusted(true);
    setBanner({ type: "", msg: "" });
    try {
      const res = await TokenChecker(`${API_PREFIX}/trusted-users/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trusted_email: newTrustedUser.email.trim() }),
      });
      const created = await jsonOrThrow(res);
      // Backend may return an invitation notice when the email isn't registered.
      if (created && created.invitation_sent) {
        setNewTrustedUser({ email: "" });
        setBanner({ type: "success", msg: created.message || "User not found invitation sent to join flowman" });
        return;
      }

      // created: { id, user:{}, trusted:{id,email,first_name,last_name}, created_at }
      const t = created.trusted || {};
      const name = [t.first_name, t.last_name].filter(Boolean).join(" ").trim() || t.email || "User";
      const newRow = {
        id: t.id,
        name,
        email: t.email || "",
        addedDate: (created.created_at || "").slice(0, 10),
      };
      setTrustedUsers((prev) => {
        // idempotent server may return existing relation — dedupe by trusted id
        const exists = prev.some((u) => u.id === newRow.id);
        return exists ? prev : [newRow, ...prev];
      });
      setNewTrustedUser({ email: "" });
      setBanner({ type: "success", msg: `${newRow.email} added as trusted.` });
    } catch (e) {
      setBanner({ type: "error", msg: e.message || "Failed to add trusted user." });
    } finally {
      setAddingTrusted(false);
    }
  };

  // Remove trust
  const handleDeleteTrustedUser = async (trustedUserId) => {
    setDeletingId(trustedUserId);
    setBanner({ type: "", msg: "" });
    try {
      await TokenChecker(`${API_PREFIX}/trusted-users/${trustedUserId}/`, { method: "DELETE" });
      setTrustedUsers((prev) => prev.filter((u) => u.id !== trustedUserId));
      setBanner({ type: "success", msg: "Trusted user removed." });
    } catch (e) {
      setBanner({ type: "error", msg: e.message || "Failed to remove trusted user." });
    } finally {
      setDeletingId(null);
    }
  };

  // ------------ Delete Account ------------
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isDeleteTextValid = () => {
    const v = deleteText.trim().toLowerCase();
    return v === "delete";
  };

  const handleConfirmDelete = async () => {
    if (!isDeleteTextValid() || deleting) return;
    setDeleting(true);
    try {
      const res = await TokenChecker(`${API_PREFIX}/delete-account/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmation: deleteText.trim() })
      });
      const data = await jsonOrThrow(res);
      alert(data.message || 'Account deleted. Goodbye!');
      try { await logout(); } catch (_) {}
    } catch (e) {
      alert(e.message || 'Failed to delete account.');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDeleteText("");
    }
  };

  return (
    <div className="flex-1 p-8 bg-[#F7FAFC]">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-2">Manage your account settings and trusted users</p>
        </div>

        {/* Account Settings */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
          <div className="p-6 border-b border-gray-200 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Account Settings</h2>
              <p className="text-gray-600">Update your personal information and security settings</p>
            </div>
            <button
              onClick={openProfileModal}
              className="text-sm rounded-md px-3 py-1.5 border transition self-start border-[#4299E1] text-[#4299E1] hover:bg-[#EBF8FF]"
            >
              Change Name and Email
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="account-name" className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <div id="account-name" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 bg-gray-100">
                  {`${accountData.firstName} ${accountData.lastName}`.trim() || "—"}
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="account-email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div id="account-email" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 bg-gray-100">
                  {accountData.email || "—"}
                </div>
                <p className="text-xs text-gray-500">
                  Changing your email requires verification and will update how you sign in.
                </p>
                {emailChangeNotice && (
                  <p className="text-xs text-green-600">{emailChangeNotice}</p>
                )}
              </div>
            </div>

            <div className="h-px w-full bg-gray-200" />

           <div className="space-y-1">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Change Password
              </h3>

              <button
                onClick={openPasswordModal}
                className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium shadow-sm active:translate-y-px transition bg-[#4299E1] text-white hover:bg-[#2B6CB0]"
              >
                Change Password
              </button>
            </div>

            <p className="text-sm text-gray-600">
              Update your password to keep your account secure.
            </p>
          </div>

        </div>
      </div>

      {/* Notification Preferences */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Notifications</h2>
          <p className="text-gray-600">Control sounds for new notifications</p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">Notification sound</div>
              <div className="text-sm text-gray-600">Play a sound when a new notification arrives</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer select-none">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={soundEnabled}
                onChange={(e) => handleSoundToggle(e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full transition-colors peer-checked:bg-[#4299E1]"></div>
              <div className="absolute w-5 h-5 bg-white rounded-full top-0.5 left-0.5 transition-transform shadow-sm peer-checked:translate-x-5"></div>
              <span className="ml-3 text-sm font-medium text-gray-900">{soundEnabled ? 'On' : 'Off'}</span>
            </label>
          </div>
        </div>
      </div>

      {/* ----- Manage Trusted Users (wired) ----- */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Manage Trusted Users</h2>
            <p className="text-gray-600">Add or remove users who can assign or invite you to tasks directly</p>
          </div>

          {/* Inline banner */}
          {banner.msg && (
            <div
              className={`mx-6 mt-4 rounded-lg px-4 py-3 text-sm ${
                banner.type === "error"
                  ? "bg-red-50 text-red-700 border border-red-200"
                  : "bg-green-50 text-green-700 border border-green-200"
              }`}
            >
              {banner.msg}
            </div>
          )}

          <div className="p-6 space-y-6">
            {/* Add New Trusted User */}
            <div className="p-4 rounded-xl border border-dashed border-[#4299E1]/40 bg-[#EBF8FF]">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Trusted User</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <label htmlFor="trustedUserEmail" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    id="trustedUserEmail"
                    type="email"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4299E1] focus:border-transparent"
                    value={newTrustedUser.email}
                    onChange={(e) => setNewTrustedUser((p) => ({ ...p, email: e.target.value }))}
                    placeholder="Enter user's email"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    onClick={handleAddTrustedUser}
                    disabled={addingTrusted}
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 font-medium text-white shadow-sm active:translate-y-px transition ${
                      addingTrusted ? "bg-[#A0AEC0] cursor-not-allowed" : "bg-[#4299E1] hover:bg-[#2B6CB0]"
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                    {addingTrusted ? "Adding..." : "Add User"}
                  </button>
                </div>
              </div>
            </div>

            {/* Existing Trusted Users */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Current Trusted Users</h3>
                <button
                  onClick={fetchTrustedUsers}
                  disabled={loadingTrusted}
                  className={`text-sm rounded-md px-3 py-1.5 border transition ${
                    loadingTrusted
                      ? "border-gray-300 text-gray-400 cursor-not-allowed"
                      : "border-[#4299E1] text-[#4299E1] hover:bg-[#EBF8FF]"
                  }`}
                >
                  {loadingTrusted ? "Refreshing..." : "Refresh"}
                </button>
              </div>

              <div className="space-y-3">
                {trustedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#EBF8FF] rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-[#4299E1]" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-600">{user.email}</div>
                        <div className="text-xs text-gray-400">Added on {user.addedDate || "—"}</div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleDeleteTrustedUser(user.id)}
                      disabled={deletingId === user.id}
                      className={`inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium active:translate-y-px transition ${
                        deletingId === user.id
                          ? "border-gray-300 text-gray-400 cursor-not-allowed"
                          : "border-red-200 text-red-600 hover:bg-red-50"
                      }`}
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="ml-2">{deletingId === user.id ? "Removing..." : "Remove"}</span>
                    </button>
                  </div>
                ))}

                {!loadingTrusted && trustedUsers.length === 0 && (
                  <div className="text-center py-10 text-gray-500 border border-gray-200 rounded-xl">
                    No trusted users added yet. Add your first trusted user above.
                  </div>
                )}

                {loadingTrusted && (
                  <div className="text-center py-10 text-gray-500 border border-gray-200 rounded-xl">
                    Loading trusted users…
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white border border-red-200 rounded-2xl shadow-sm">
          <div className="p-6 border-b border-red-200">
            <h2 className="text-xl font-semibold text-gray-900">Danger Zone</h2>
            <p className="text-gray-600">Permanently delete your account and all associated data.</p>
          </div>
          <div className="p-6 flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">Delete Account</div>
              <div className="text-sm text-gray-600">This action cannot be undone.</div>
            </div>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium border-red-300 text-red-700 hover:bg-red-50 active:translate-y-px transition"
            >
              <Trash2 className="w-4 h-4" />
              <span className="ml-2">Delete Account</span>
            </button>
          </div>
        </div>

        {showProfileModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm px-4">
            <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="relative px-6 py-5 text-white">
                <div className="absolute inset-0 bg-gradient-to-r from-[#10A7F3] via-[#38BDF8] to-[#10F3D6]"></div>
                <div className="relative">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/80">Account</p>
                  <h3 className="text-lg font-semibold">Change name and email</h3>
                </div>
              </div>
              <div className="px-6 py-5">
                {profileStep === 1 && (
                  <>
                    <p className="text-sm text-slate-600">
                      Enter your current password to continue.
                    </p>
                    <div className="mt-4 space-y-2">
                      <label htmlFor="profilePassword" className="block text-sm font-medium text-gray-700">
                        Current password
                      </label>
                      <input
                        id="profilePassword"
                        type="password"
                        value={profilePassword}
                        onChange={(e) => setProfilePassword(e.target.value)}
                        placeholder="Enter your current password"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4299E1] focus:border-transparent"
                      />
                    </div>
                    <div className="mt-6 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={closeProfileModal}
                        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleProfilePasswordNext}
                        className="rounded-lg bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
                      >
                        Continue
                      </button>
                    </div>
                  </>
                )}

                {profileStep === 2 && (
                  <>
                    <p className="text-sm text-slate-600">
                      Update your name or email below.
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">First name</label>
                        <input
                          type="text"
                          value={profileFirstName}
                          onChange={(e) => setProfileFirstName(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4299E1] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Last name</label>
                        <input
                          type="text"
                          value={profileLastName}
                          onChange={(e) => setProfileLastName(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4299E1] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">New email</label>
                        <input
                          type="email"
                          value={profileEmail}
                          onChange={(e) => setProfileEmail(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4299E1] focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-6 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={closeProfileModal}
                        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveProfile}
                        disabled={savingAccount}
                        className={`rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition ${
                          savingAccount ? "bg-slate-300 cursor-not-allowed" : "bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] hover:opacity-90"
                        }`}
                      >
                        {savingAccount ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </>
                )}

                {profileStep === 3 && (
                  <>
                    <p className="text-sm text-slate-600">
                      {profileMessage || "Your changes have been saved."}
                    </p>
                    {profileMessage && profileMessage.toLowerCase().includes("verification") && (
                      <p className="mt-3 text-xs text-slate-500">
                        Check your inbox and follow the link to complete the email change.
                      </p>
                    )}
                    <div className="mt-6 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={closeProfileModal}
                        className="rounded-lg bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
                      >
                        Done
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {showPasswordModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm px-4">
            <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="relative px-6 py-5 text-white">
                <div className="absolute inset-0 bg-gradient-to-r from-[#10A7F3] via-[#38BDF8] to-[#10F3D6]"></div>
                <div className="relative">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/80">Account</p>
                  <h3 className="text-lg font-semibold">Change password</h3>
                </div>
              </div>
              <div className="px-6 py-5">
                {passwordStep === 1 && (
                  <>
                    <p className="text-sm text-slate-600">Enter your current password to continue.</p>
                    <div className="mt-4 space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Current password</label>
                      <input
                        type="password"
                        value={passwordCurrent}
                        onChange={(e) => setPasswordCurrent(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4299E1] focus:border-transparent"
                      />
                    </div>
                    <div className="mt-6 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={closePasswordModal}
                        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handlePasswordNext}
                        className="rounded-lg bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
                      >
                        Continue
                      </button>
                    </div>
                  </>
                )}

                {passwordStep === 2 && (
                  <>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">New password</label>
                        <input
                          type="password"
                          value={passwordNew}
                          onChange={(e) => setPasswordNew(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4299E1] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Confirm password</label>
                        <input
                          type="password"
                          value={passwordConfirm}
                          onChange={(e) => setPasswordConfirm(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4299E1] focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-6 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={closePasswordModal}
                        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSavePassword}
                        disabled={savingAccount}
                        className={`rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition ${
                          savingAccount ? "bg-slate-300 cursor-not-allowed" : "bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] hover:opacity-90"
                        }`}
                      >
                        {savingAccount ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </>
                )}

                {passwordStep === 3 && (
                  <>
                    <p className="text-sm text-slate-600">
                      {passwordMessage || "Password updated successfully."}
                    </p>
                    <div className="mt-6 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={closePasswordModal}
                        className="rounded-lg bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
                      >
                        Done
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md border border-gray-200">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Account Deletion</h3>
                <p className="mt-2 text-sm text-gray-600">
                  This will permanently delete your account and data. Type
                  <span className="font-semibold text-gray-900"> "delete"</span> to confirm.
                </p>
                <input
                  autoFocus
                  className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                  placeholder="Type the confirmation phrase"
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                />

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={() => { if (!deleting) { setShowDeleteModal(false); setDeleteText(""); } }}
                    className="rounded-lg px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={!isDeleteTextValid() || deleting}
                    className={`rounded-lg px-4 py-2 font-medium text-white shadow-sm active:translate-y-px transition ${
                      !isDeleteTextValid() || deleting ? 'bg-red-300 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {deleting ? 'Deleting…' : 'Delete Account'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
